'use client';

import { forwardRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { photoCarouselDotRecipe } from './styles/photo-carousel-dot.styles';
import type { PhotoCarouselDotPropsInterface } from './types/photo-carousel-dot.types';

/**
 * Used by `PhotoCarousel.Dots` to render an individual dot.
 *
 * Renders as an `li` inside `PhotoCarousel.Dots`' list [CDS-5859]. Dots are
 * decorative: they visualize progress through the slides but are not interactive,
 * and `PhotoCarousel.Dots` hides them from assistive technologies. Navigation is
 * handled by `PhotoCarousel.PreviousButton` and `PhotoCarousel.NextButton`.
 *
 * @since 8.66.0
 */

export const PhotoCarouselDot = forwardRef<HTMLLIElement, PhotoCarouselDotPropsInterface>(
  (props: PhotoCarouselDotPropsInterface, forwardedRef) => {
    const {
      'aria-controls': _ariaControls,
      'aria-label': _ariaLabel,
      'aria-selected': ariaSelected,
      className,
      css: cssProp,
      isActive,
      isContinuation = false,
      isHidden = false,
      ...rest
    } = props;

    const styles = css(
      // `aria-selected` is the deprecated v10 spelling of `isActive`; still honored
      // so `renderDot` consumers spreading the old `dotProps` keep rendering correctly.
      photoCarouselDotRecipe.raw({
        selected: isActive ?? !!ariaSelected,
        isContinuation,
        isHidden,
      }),
      cssProp,
    );

    return (
      <li
        className={cx('c11n', 'c11n_photo-carousel__dot', styles, className)}
        data-c11n-component="PhotoCarousel.Dot"
        data-c11n-version={__C11N_VERSION__}
        ref={forwardedRef}
        {...rest}
      />
    );
  },
);

PhotoCarouselDot.displayName = 'PhotoCarousel.Dot';
